/**
 * BloodworkSection
 *
 * Genius-tier Bloodwork feature: upload lab PDF/photo, review OCR results,
 * save to server, view latest summary, trends, and AI insight.
 *
 * Renders inline inside MIQView (matches WorkoutPlanner / PeptideCalculator pattern).
 */

import React, { useEffect, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FLAG_COLORS = {
  high:   { bg: 'rgba(239, 68, 68, 0.15)',  text: '#ef4444', border: 'rgba(239, 68, 68, 0.4)', label: 'High' },
  low:    { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6', border: 'rgba(59, 130, 246, 0.4)', label: 'Low' },
  normal: { bg: 'rgba(34, 197, 94, 0.12)',  text: '#22c55e', border: 'rgba(34, 197, 94, 0.3)',  label: 'Normal' },
};

const CATEGORY_COLORS = {
  lipids:       '#ef4444',
  metabolic:    '#f97316',
  inflammation: '#eab308',
  thyroid:      '#8b5cf6',
  vitamins:     '#22c55e',
  minerals:     '#06b6d4',
  hormones:     '#ec4899',
  kidney:       '#3b82f6',
  liver:        '#a855f7',
};

const CATEGORY_LABELS = {
  lipids: 'Lipids', metabolic: 'Metabolic', inflammation: 'Inflammation',
  thyroid: 'Thyroid', vitamins: 'Vitamins', minerals: 'Minerals',
  hormones: 'Hormones', kidney: 'Kidney', liver: 'Liver',
};

const MARKER_KEY_OPTIONS = [
  { key: 'total_cholesterol', label: 'Total Cholesterol', unit: 'mg/dL', category: 'lipids' },
  { key: 'ldl',               label: 'LDL',               unit: 'mg/dL', category: 'lipids' },
  { key: 'hdl',               label: 'HDL',               unit: 'mg/dL', category: 'lipids' },
  { key: 'triglycerides',     label: 'Triglycerides',     unit: 'mg/dL', category: 'lipids' },
  { key: 'hba1c',             label: 'HbA1c',             unit: '%',     category: 'metabolic' },
  { key: 'fasting_glucose',   label: 'Fasting Glucose',   unit: 'mg/dL', category: 'metabolic' },
  { key: 'insulin',           label: 'Insulin',           unit: 'µIU/mL', category: 'metabolic' },
  { key: 'hs_crp',            label: 'hs-CRP',            unit: 'mg/L',  category: 'inflammation' },
  { key: 'tsh',               label: 'TSH',               unit: 'mIU/L', category: 'thyroid' },
  { key: 'free_t4',           label: 'Free T4',           unit: 'ng/dL', category: 'thyroid' },
  { key: 'vitamin_d',         label: 'Vitamin D',         unit: 'ng/mL', category: 'vitamins' },
  { key: 'vitamin_b12',       label: 'Vitamin B12',       unit: 'pg/mL', category: 'vitamins' },
  { key: 'ferritin',          label: 'Ferritin',          unit: 'ng/mL', category: 'minerals' },
  { key: 'iron',              label: 'Iron',              unit: 'µg/dL', category: 'minerals' },
  { key: 'testosterone',      label: 'Testosterone',      unit: 'ng/dL', category: 'hormones' },
  { key: 'cortisol',          label: 'Cortisol',          unit: 'µg/dL', category: 'hormones' },
  { key: 'creatinine',        label: 'Creatinine',        unit: 'mg/dL', category: 'kidney' },
  { key: 'egfr',              label: 'eGFR',              unit: 'mL/min/1.73m²', category: 'kidney' },
  { key: 'alt',               label: 'ALT',               unit: 'U/L',   category: 'liver' },
  { key: 'ast',               label: 'AST',               unit: 'U/L',   category: 'liver' },
];

const MarkerRow = ({ marker, onChange, onRemove, editable }) => {
  const flag = FLAG_COLORS[marker.flag] || null;
  return (
    <div
      data-testid={`bw-marker-row-${marker.key || 'custom'}`}
      style={{
        display: 'grid',
        gridTemplateColumns: editable ? '1.4fr 0.8fr 0.5fr 1fr 0.6fr 24px' : '1.4fr 0.6fr 0.6fr 1fr 0.8fr',
        gap: '8px',
        alignItems: 'center',
        padding: '10px 6px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
        {marker.marker}
      </div>
      {editable ? (
        <input
          type="number"
          step="any"
          value={marker.value ?? ''}
          onChange={(e) => onChange({ ...marker, value: e.target.value === '' ? null : parseFloat(e.target.value) })}
          data-testid={`bw-value-${marker.key || 'custom'}`}
          style={inputStyle}
        />
      ) : (
        <div style={{ fontSize: '14px', fontWeight: 700 }}>{marker.value ?? '—'}</div>
      )}
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{marker.unit || '—'}</div>
      <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
        ref: {marker.reference_range || '—'}
      </div>
      {flag ? (
        <span style={{
          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
          padding: '2px 8px', borderRadius: '999px',
          background: flag.bg, color: flag.text, border: `1px solid ${flag.border}`,
          textAlign: 'center',
        }}>{flag.label}</span>
      ) : <span />}
      {editable && (
        <button
          type="button"
          onClick={() => onRemove(marker)}
          data-testid={`bw-remove-${marker.key || 'custom'}`}
          aria-label="Remove marker"
          style={{
            background: 'transparent', border: 'none', color: '#ef4444',
            cursor: 'pointer', fontSize: '16px', padding: 0,
          }}
        >×</button>
      )}
    </div>
  );
};

const inputStyle = {
  padding: '6px 8px',
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid var(--border-color)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  width: '100%',
};

const BloodworkSection = ({ userId, isPremium, userTier, onAskCoach }) => {
  const [expanded, setExpanded] = useState(false);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState({});
  const [insight, setInsight] = useState('');
  const [focus, setFocus] = useState(null);
  const [focusDismissed, setFocusDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Upload / review flow state
  const [reviewMarkers, setReviewMarkers] = useState(null); // null when not in review, else array
  const [reviewMeta, setReviewMeta] = useState({ source_type: 'photo', source_filename: '', lab_date: '' , extraction_confidence: 0.0, raw_ocr_text: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);

  const isGenius = isPremium || userTier === 'genius' || userTier === 'miq';

  const loadAll = useCallback(async () => {
    if (!userId || !isGenius) return;
    setLoading(true);
    try {
      const [l, h, t, f] = await Promise.all([
        fetch(`${BACKEND_URL}/api/biomarkers/latest/${userId}`).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/biomarkers/history/${userId}?limit=20`).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/biomarkers/trends/${userId}`).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/bloodwork-nutrition-focus/${userId}`).then((r) => r.ok ? r.json() : null),
      ]);
      setLatest(l);
      setHistory(h?.entries || []);
      setTrends(t?.series || {});
      setFocus(f);
    } catch (_e) { /* silent */ }
    setLoading(false);
  }, [userId, isGenius]);

  useEffect(() => {
    if (expanded && isGenius) loadAll();
  }, [expanded, isGenius, loadAll]);

  const loadInsight = async () => {
    if (!userId || !isGenius) return;
    setInsight('Generating your lab insight…');
    try {
      const res = await fetch(`${BACKEND_URL}/api/biomarkers/insight/${userId}`);
      const data = await res.json();
      setInsight(data?.insight || 'No insight available yet.');
    } catch (_e) {
      setInsight('Could not load insight. Try again shortly.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('user_id', userId);
      form.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/biomarkers/upload-pdf`, {
        method: 'POST', body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err?.detail || {};
        if (detail?.reason === 'tier_locked') {
          setUploadError('Bloodwork is a Genius feature. Please upgrade to upload lab reports.');
        } else {
          setUploadError(typeof detail === 'string' ? detail : (detail?.message || `Upload failed (HTTP ${res.status}).`));
        }
        return;
      }
      const data = await res.json();
      setReviewMarkers(data?.extracted_markers || []);
      setReviewMeta({
        source_type: data?.source_type || 'photo',
        source_filename: data?.source_filename || file.name,
        lab_date: data?.lab_date || new Date().toISOString().slice(0, 10),
        extraction_confidence: data?.extraction_confidence ?? 0.0,
        raw_ocr_text: data?.raw_ocr_text || '',
      });
    } catch (e2) {
      setUploadError(e2?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      // reset the input so re-selecting the same file re-triggers change
      if (e.target) e.target.value = '';
    }
  };

  const addManualMarker = () => {
    const opt = MARKER_KEY_OPTIONS[0];
    setReviewMarkers([...(reviewMarkers || []), {
      marker: opt.label, key: opt.key, value: null, unit: opt.unit,
      reference_range: null, flag: null, category: opt.category,
    }]);
  };

  const updateMarker = (idx, next) => {
    const copy = [...(reviewMarkers || [])];
    copy[idx] = next;
    setReviewMarkers(copy);
  };

  const removeMarker = (idx) => {
    const copy = [...(reviewMarkers || [])];
    copy.splice(idx, 1);
    setReviewMarkers(copy);
  };

  const saveEntry = async () => {
    if (!reviewMarkers || reviewMarkers.length === 0) return;
    setSaving(true);
    try {
      const cleaned = reviewMarkers
        .filter((m) => m.value !== null && m.value !== '')
        .map((m) => ({
          marker: m.marker, key: m.key || null, value: parseFloat(m.value),
          unit: m.unit || null, reference_range: m.reference_range || null,
          flag: m.flag || null, category: m.category || null,
        }));
      const body = {
        user_id: userId,
        source_type: reviewMeta.source_type,
        source_filename: reviewMeta.source_filename,
        lab_date: reviewMeta.lab_date || null,
        extracted_markers: cleaned,
        extraction_confidence: reviewMeta.extraction_confidence,
        raw_ocr_text: reviewMeta.raw_ocr_text,
      };
      const res = await fetch(`${BACKEND_URL}/api/biomarkers/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      setReviewMarkers(null);
      await loadAll();
    } catch (e) {
      setUploadError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const cancelReview = () => {
    setReviewMarkers(null);
    setUploadError('');
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this lab entry?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/biomarkers/entry/${entryId}?user_id=${userId}`, { method: 'DELETE' });
      await loadAll();
    } catch (_e) { /* silent */ }
  };

  const askCoachAboutLabs = () => {
    if (typeof onAskCoach !== 'function') return;
    // Prefer highlighting an out-of-range marker if present
    const markers = latest?.entry?.extracted_markers || [];
    const flagged = markers.find((m) => m.flag === 'high' || m.flag === 'low');
    const question = flagged
      ? `Based on my latest labs — my ${flagged.marker} is ${flagged.value} ${flagged.unit || ''} (${flagged.flag}) — what should I eat this week to move it?`
      : `Based on my latest lab results, what dietary changes would give me the biggest health upgrade?`;
    onAskCoach(question);
  };

  const exportPdf = async () => {
    if (!userId || exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/biomarkers/export-pdf/${userId}`);
      if (!res.ok) {
        setUploadError(res.status === 404
          ? 'No bloodwork entries to export yet.'
          : `Export failed (HTTP ${res.status}).`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bloodwork_summary_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setUploadError(e?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const outOfRangeCount = latest?.out_of_range_count ?? 0;
  const totalMarkers = latest?.marker_count ?? 0;

  if (!isGenius) {
    // Genius upsell card
    return (
      <div
        data-testid="bloodwork-section-locked"
        style={{
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          border: '1px solid rgba(220, 38, 38, 0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
          }}>🩸</div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>Bloodwork · Locked</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Upload lab PDFs and get personalized insights — Genius only
            </div>
          </div>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
          Upload lab PDFs or photos and Genius will read your markers, flag what&apos;s off, track trends, and personalize your nutrition + workouts.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="bloodwork-section"
      style={{
        background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid rgba(220, 38, 38, 0.2)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        data-testid="bloodwork-header"
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px', flexShrink: 0,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
          }}>🩸</div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>Bloodwork</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {latest?.has_data
                ? `${totalMarkers} markers · ${outOfRangeCount} out of range · ${latest?.entry?.lab_date || latest?.entry?.created_at?.slice(0, 10) || ''}`
                : 'Upload your first lab report'}
            </div>
          </div>
        </div>
        <span style={{ fontSize: '20px', color: 'var(--text-secondary)' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: '16px' }}>
          {/* Auto-Sync Nutrition Focus banner */}
          {focus?.active && !focusDismissed && (
            <div
              data-testid="bloodwork-focus-banner"
              style={{
                marginBottom: '14px',
                padding: '12px 14px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>🎯</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#9333ea', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Auto-Sync · {focus.labels?.join(' + ') || 'Active'}
                  </div>
                  <div style={{ fontSize: '12px', lineHeight: 1.5, color: 'var(--text-primary)' }}>
                    {focus.reason}
                  </div>
                  {focus.prefer_keywords?.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                      <strong style={{ color: '#22c55e' }}>Prefer:</strong> {focus.prefer_keywords.slice(0, 6).join(', ')}
                      {focus.prefer_keywords.length > 6 && '…'}
                      <br />
                      <strong style={{ color: '#ef4444' }}>Avoid:</strong> {focus.avoid_keywords?.slice(0, 6).join(', ')}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFocusDismissed(true)}
                  data-testid="bloodwork-focus-dismiss"
                  aria-label="Dismiss"
                  style={{
                    background: 'transparent', border: 'none', color: 'var(--text-secondary)',
                    fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1,
                  }}
                >×</button>
              </div>
            </div>
          )}

          {/* Review & Edit modal (after OCR or manual add) */}
          {reviewMarkers ? (
            <div data-testid="bloodwork-review-panel" style={{
              background: 'var(--bg-secondary)', borderRadius: '12px', padding: '14px',
              border: '1px solid var(--border-color)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>
                Review your extracted markers
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Lab date
                  <input
                    type="date" value={reviewMeta.lab_date}
                    onChange={(e) => setReviewMeta({ ...reviewMeta, lab_date: e.target.value })}
                    data-testid="bw-lab-date"
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Confidence
                  <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 600 }}>
                    {Math.round((reviewMeta.extraction_confidence || 0) * 100)}%
                  </div>
                </label>
              </div>

              {reviewMarkers.length === 0 ? (
                <div style={{ padding: '12px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  No markers detected. Add one manually below.
                </div>
              ) : (
                <div style={{ marginTop: '4px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 0.8fr 0.5fr 1fr 0.6fr 24px',
                    gap: '8px', padding: '4px 6px 6px',
                    fontSize: '10px', color: 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                  }}>
                    <div>Marker</div><div>Value</div><div>Unit</div><div>Reference</div><div>Flag</div><div />
                  </div>
                  {reviewMarkers.map((m, i) => (
                    <MarkerRow
                      key={`review-${i}`} marker={m} editable
                      onChange={(next) => updateMarker(i, next)}
                      onRemove={() => removeMarker(i)}
                    />
                  ))}
                </div>
              )}

              <button
                type="button" onClick={addManualMarker}
                data-testid="bw-add-marker-btn"
                style={{
                  marginTop: '8px', padding: '6px 12px', fontSize: '12px',
                  borderRadius: '8px', border: '1px dashed var(--border-color)',
                  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >
                + Add marker manually
              </button>

              {uploadError && (
                <div style={{
                  marginTop: '10px', padding: '8px', borderRadius: '6px',
                  background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontSize: '12px',
                }}>{uploadError}</div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                <button
                  type="button" onClick={cancelReview}
                  data-testid="bw-cancel-btn"
                  style={{
                    flex: 1, padding: '10px', fontSize: '13px', fontWeight: 600,
                    borderRadius: '8px', border: '1px solid var(--border-color)',
                    background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer',
                  }}
                >Cancel</button>
                <button
                  type="button" onClick={saveEntry} disabled={saving}
                  data-testid="bw-save-entry-btn"
                  style={{
                    flex: 2, padding: '10px', fontSize: '13px', fontWeight: 700,
                    borderRadius: '8px', border: 'none',
                    background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
                    color: 'white', cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >{saving ? 'Saving…' : 'Save entry'}</button>
              </div>
            </div>
          ) : (
            <>
              {/* Upload buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <label
                  data-testid="bw-upload-label"
                  style={{
                    flex: 1, padding: '12px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                    borderRadius: '10px', fontSize: '13px', fontWeight: 700,
                    background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
                    color: 'white', border: 'none',
                    opacity: uploading ? 0.6 : 1,
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                  }}
                >
                  {uploading ? 'Reading with AI…' : '📄 Upload lab PDF / photo'}
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    disabled={uploading}
                    data-testid="bw-upload-input"
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setReviewMarkers([]);
                    setReviewMeta({
                      source_type: 'manual', source_filename: '',
                      lab_date: new Date().toISOString().slice(0, 10),
                      extraction_confidence: 1.0, raw_ocr_text: '',
                    });
                  }}
                  data-testid="bw-manual-entry-btn"
                  style={{
                    padding: '12px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                    border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)', cursor: 'pointer',
                  }}
                >✍️ Manual</button>
              </div>

              {uploadError && (
                <div style={{
                  marginBottom: '10px', padding: '8px', borderRadius: '6px',
                  background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontSize: '12px',
                }}>{uploadError}</div>
              )}

              {/* Latest results */}
              {latest?.has_data && latest?.entry && (
                <div data-testid="bw-latest-results" style={{
                  background: 'var(--bg-secondary)', borderRadius: '12px', padding: '14px',
                  border: '1px solid var(--border-color)', marginBottom: '12px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>
                      Latest results · {latest.entry.lab_date || latest.entry.created_at?.slice(0, 10)}
                    </div>
                    <button
                      type="button" onClick={() => deleteEntry(latest.entry.entry_id)}
                      data-testid="bw-delete-latest-btn"
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}
                    >Delete</button>
                  </div>
                  {(latest.entry.extracted_markers || []).map((m, i) => (
                    <MarkerRow key={`latest-${i}`} marker={m} editable={false} />
                  ))}

                  {/* Category pills */}
                  {Object.keys(trends).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                      {Array.from(new Set(
                        (latest.entry.extracted_markers || []).map((m) => m.category).filter(Boolean)
                      )).map((cat) => (
                        <span key={cat} style={{
                          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                          padding: '3px 8px', borderRadius: '999px',
                          background: `${CATEGORY_COLORS[cat] || '#666'}20`,
                          color: CATEGORY_COLORS[cat] || '#666',
                          border: `1px solid ${CATEGORY_COLORS[cat] || '#666'}40`,
                        }}>{CATEGORY_LABELS[cat] || cat}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Trends */}
              {latest?.has_data && Object.keys(trends).length > 0 && (
                <div data-testid="bw-trends" style={{
                  background: 'var(--bg-secondary)', borderRadius: '12px', padding: '14px',
                  border: '1px solid var(--border-color)', marginBottom: '12px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Trends</div>
                  {Object.entries(trends).slice(0, 6).map(([key, points]) => {
                    if (!points || points.length < 2) return null;
                    const opt = MARKER_KEY_OPTIONS.find((o) => o.key === key);
                    const last = points[points.length - 1];
                    const prev = points[points.length - 2];
                    const delta = (last.value - prev.value).toFixed(2);
                    const up = delta > 0;
                    return (
                      <div key={key} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 600 }}>{opt?.label || key}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {prev.value} → <strong>{last.value}</strong> {last.unit || ''}
                          <span style={{ marginLeft: '6px', color: up ? '#ef4444' : '#22c55e' }}>
                            {up ? '▲' : '▼'} {Math.abs(delta)}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              {latest?.has_data && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button" onClick={loadInsight}
                    data-testid="bw-get-insight-btn"
                    style={{
                      flex: '1 1 30%', padding: '10px', fontSize: '12px', fontWeight: 700,
                      borderRadius: '8px', border: '1px solid rgba(147, 51, 234, 0.4)',
                      background: 'rgba(147, 51, 234, 0.12)', color: '#9333ea', cursor: 'pointer',
                    }}
                  >🧠 AI insight</button>
                  <button
                    type="button" onClick={askCoachAboutLabs}
                    data-testid="bw-ask-coach-btn"
                    disabled={typeof onAskCoach !== 'function'}
                    style={{
                      flex: '1 1 30%', padding: '10px', fontSize: '12px', fontWeight: 700,
                      borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.4)',
                      background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6',
                      cursor: typeof onAskCoach === 'function' ? 'pointer' : 'not-allowed',
                      opacity: typeof onAskCoach === 'function' ? 1 : 0.5,
                    }}
                  >💬 Ask Coach</button>
                  <button
                    type="button" onClick={exportPdf} disabled={exporting}
                    data-testid="bw-export-pdf-btn"
                    style={{
                      flex: '1 1 30%', padding: '10px', fontSize: '12px', fontWeight: 700,
                      borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.4)',
                      background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e',
                      cursor: exporting ? 'wait' : 'pointer',
                      opacity: exporting ? 0.7 : 1,
                    }}
                  >{exporting ? '⏳ Exporting…' : '📄 Export for doctor'}</button>
                </div>
              )}

              {insight && (
                <div data-testid="bw-insight" style={{
                  marginTop: '12px', padding: '12px', borderRadius: '10px',
                  background: 'rgba(147, 51, 234, 0.08)', border: '1px solid rgba(147, 51, 234, 0.25)',
                  fontSize: '13px', lineHeight: 1.6, color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                }}>
                  {insight}
                </div>
              )}

              {/* History */}
              {history.length > 1 && (
                <div data-testid="bw-history" style={{ marginTop: '14px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600 }}>
                    Previous entries ({history.length - 1})
                  </div>
                  {history.slice(1, 6).map((entry) => (
                    <div key={entry.entry_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: '12px',
                    }}>
                      <span>{entry.lab_date || entry.created_at?.slice(0, 10)}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {entry.extracted_markers?.length || 0} markers
                      </span>
                      <button
                        type="button" onClick={() => deleteEntry(entry.entry_id)}
                        style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}
                      >Delete</button>
                    </div>
                  ))}
                </div>
              )}

              {!latest?.has_data && !uploading && (
                <div style={{
                  textAlign: 'center', padding: '20px 12px', color: 'var(--text-secondary)', fontSize: '12px',
                }}>
                  Upload your first lab report to unlock personalized bloodwork insights.
                </div>
              )}

              {loading && !latest && (
                <div style={{ textAlign: 'center', padding: '12px', color: 'var(--text-secondary)', fontSize: '12px' }}>
                  Loading your bloodwork…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BloodworkSection;
